import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FlaskConical, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { RunStatusBadge } from '@/components/model-lab/badges'
import { NewRunDialog } from '@/components/model-lab/NewRunDialog'
import { useGHLLocationNames } from '@/hooks/useGHLDashboard'
import { useModelLabRuns } from '@/hooks/useModelLab'
import { contestantKey, contestantLabel, TASK_META } from '@/lib/model-lab'

export default function ModelLabPage() {
  const navigate = useNavigate()
  const runs = useModelLabRuns()
  const locationNames = useGHLLocationNames()
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <FlaskConical className="h-6 w-6" /> Model Lab
          </h1>
          <p className="text-sm text-muted-foreground">
            Replay real middleware decisions through Anthropic, OpenAI, DeepSeek and GLM models, label the right
            answers, and see which model gets them right.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> New run
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Decisions</TableHead>
                <TableHead>Models</TableHead>
                <TableHead>Calls</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.isLoading &&
                Array.from({ length: 3 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              {runs.error && (
                <TableRow>
                  <TableCell colSpan={6} className="text-sm text-destructive">
                    {(runs.error as Error).message}
                  </TableCell>
                </TableRow>
              )}
              {runs.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                    No runs yet. Start one to compare models on a location's conversations.
                  </TableCell>
                </TableRow>
              )}
              {runs.data?.map((run) => (
                <TableRow key={run.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/admin/model-lab/runs/${run.id}`)}>
                  <TableCell className="whitespace-nowrap text-sm">{new Date(run.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{locationNames.data?.get(run.location_id) ?? run.location_id}</TableCell>
                  <TableCell className="text-xs">{run.tasks.map((t) => TASK_META[t].label).join(', ')}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {run.contestants.map((c) => contestantLabel(contestantKey(c))).join(', ')}
                  </TableCell>
                  <TableCell className="text-sm">{run.total_calls || '—'}</TableCell>
                  <TableCell>
                    <RunStatusBadge status={run.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <NewRunDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  )
}
